<div class="container-fluid">
  <div class="row">
    <div class="col-12 px-0">
      <div class="card p-0 border-0 shadow-none">
        <div class="card-body p-3">
          <!-- Header Section -->
          <div class="d-flex flex-row flex-wrap justify-content-between align-items-center mb-4">
            <div>
              <h1 class="card-title mb-1">Product Inventory</h1>
              <span class="sub-text">Manage, categorize, and track company stock levels</span>
            </div>

            <div class="d-flex gap-2">
              <button
                class="btn btn-soft-primary d-flex align-items-center px-3 py-2"
                (click)="onManageCategories()"
                title="Manage Categories">
                <i class="bi bi-tags"></i>
                <span>Categories</span>
              </button>
              <button
                class="btn btn-soft-primary d-flex align-items-center px-3 py-2"
                (click)="onManageFormulationTypes()"
                title="Manage Formulation Types">
                <i class="bi bi-droplet"></i>
                <span>Formulations</span>
              </button>
              <button
                class="btn btn-soft-primary d-flex align-items-center gap-2 px-3 py-2"
                (click)="onExportProducts()"
                [disabled]="isExportingProducts">
                @if (isExportingProducts) {
                  <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Exporting...
                } @else {
                  <i class="bi bi-download"></i>
                  Export Products
                }
              </button>
              <button class="btn btn-primary d-flex align-items-center px-4 py-2" (click)="onAddNewProduct()">
                <i class="bi bi-plus-circle"></i>
                <span>Add New Product</span>
              </button>
            </div>
          </div>

          <!-- Modern Filter Bar -->
          <div class="bg-light p-3 border mb-4">
            <div class="row g-3">
              <div class="col-lg-6">
                <div class="input-group">
                  <span class="input-group-text bg-white border-end-0 ps-3 rounded-0">
                    <i class="bi bi-search text-primary"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control border-start-0 py-2 ps-2 rounded-0"
                    placeholder="Search by product name or HSN code..."
                    [(ngModel)]="searchTerm"
                    (keyup)="onSearchKeyUp()" />
                </div>
              </div>

              <div class="col-lg-3">
                <ng-select
                  placeholder="All Categories"
                  [items]="productCategories"
                  [(ngModel)]="selectedCategory"
                  bindValue="id"
                  bindLabel="name"
                  [clearable]="true"
                  [searchable]="false"
                  (ngModelChange)="onCategoryChange()" />
              </div>

              <div class="col-lg-3">
                <ng-select
                  placeholder="All Companies"
                  [items]="companies"
                  [(ngModel)]="selectedCompany"
                  bindValue="id"
                  bindLabel="name"
                  [clearable]="true"
                  [searchable]="false"
                  (ngModelChange)="onChangeCompany()" />
              </div>
            </div>
          </div>

          @if (isDataLoading) {
            <app-loading-spinner></app-loading-spinner>
          } @else {
            @if ((products || []).length === 0) {
              <div class="empty-state-container">
                <i class="bi bi-box-seam"></i>
                <h5>No Products Found</h5>
                <p>Try adjusting your filters or search term to find what you're looking for.</p>
                <button
                  class="btn btn-outline-primary mt-3"
                  (click)="searchTerm = ''; selectedCategory = ''; selectedCompany = ''; _getProductList()">
                  Reset All Filters
                </button>
              </div>
            } @else {
              <table class="table table-hover align-middle mb-0">
                <thead class="bg-light">
                  <tr>
                    <th class="border-0 text-uppercase small fw-bold text-muted py-3">Product Info</th>
                    <th class="border-0 text-uppercase small fw-bold text-muted py-3">HSN / Category</th>
                    <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Formulation</th>
                    <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-center">Company</th>
                    <th class="border-0 text-uppercase small fw-bold text-muted py-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  @for (product of products; track product.id) {
                    <tr class="cursor-pointer transition-all border-bottom" [routerLink]="['/products', product.id]">
                      <td class="py-3">
                        <div class="d-flex align-items-center">
                          <div
                            class="product-icon-box me-3 rounded-3 d-flex align-items-center justify-content-center text-primary bg-primary bg-opacity-10">
                            <i class="bi bi-box fs-5"></i>
                          </div>
                          <div>
                            <div class="fw-bold text-dark">{{ product.name }}</div>
                            <div class="small text-muted">
                              {{ product.contents || 'No description' | slice: 0 : 50
                              }}{{ (product.contents.length || 0) > 50 ? '...' : '' }}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td class="py-3">
                        <div class="d-flex flex-column">
                          <span class="fw-semibold text-dark">{{ product.hsn_code || 'N/A' }}</span>
                          <span class="app-badge badge-primary align-self-start mt-1">
                            {{ product.product_category?.name || 'Uncategorized' }}
                          </span>
                        </div>
                      </td>
                      <td class="py-3 text-center">
                        @if (product.formulation_type?.label; as formulation) {
                          <span class="app-badge" [ngClass]="formulationBadgeClass(product)">{{ formulation }}</span>
                        } @else {
                          <span class="text-black">-</span>
                        }
                      </td>
                      <td class="py-3 text-center">
                        @if (product.company) {
                          <span class="text-muted small">
                            <i class="bi bi-building me-1"></i>
                            {{ product.company.name }}
                          </span>
                        } @else {
                          <span class="text-black">-</span>
                        }
                      </td>
                      <td class="py-3 text-end" (click)="$event.stopPropagation()">
                        <div class="d-flex gap-2 justify-content-end">
                          <button class="btn btn-action-edit" (click)="onEditProduct(product)" title="Edit product">
                            <i class="bi bi-pencil-square"></i>
                          </button>
                          <button
                            class="btn btn-action-delete"
                            (click)="onDeleteProduct(product.id)"
                            title="Delete product">
                            <i class="bi bi-trash3"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>

              <div class="mt-4">
                <app-pagination
                  [currentPage]="currentPage"
                  [totalCount]="totalCount || 0"
                  [pageSize]="10"
                  [hasNextPage]="hasNextPage"
                  [hasPreviousPage]="hasPreviousPage"
                  (pageChange)="onPageChange($event)"></app-pagination>
              </div>
            }
          }
        </div>
      </div>
    </div>
  </div>
</div>

<ng-template #manageCategoriesModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <div class="modal-header py-3">
      <h5 class="modal-title">Manage Categories</h5>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>

    <div class="modal-body p-4">
      <!-- Add new category -->
      @if (!isAddingNewCategory) {
        <button type="button" class="btn btn-primary mb-3" (click)="onToggleAddCategory()">
          <i class="bi bi-plus-circle"></i>
          Add New Category
        </button>
      } @else {
        <div class="d-flex gap-2 mb-3">
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder="Category name"
            [(ngModel)]="newCategoryName" />
          <button
            type="button"
            class="btn btn-primary btn-sm text-nowrap"
            [disabled]="!newCategoryName.trim() || isCreatingCategory"
            (click)="onCreateCategory(false)">
            @if (isCreatingCategory) {
              <app-loading-spinner [inline]="true" [textClass]="'text-white'"></app-loading-spinner>
            } @else {
              Create
            }
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onToggleAddCategory()">Cancel</button>
        </div>
      }

      <!-- Category list -->
      @if (productCategories.length === 0) {
        <p class="text-muted small text-center py-3 mb-0">No categories yet.</p>
      } @else {
        <ul class="list-group list-group-flush">
          @for (category of productCategories; track category.id) {
            <li class="list-group-item d-flex justify-content-between align-items-center px-0">
              <span>{{ category.name }}</span>
              <button
                type="button"
                class="btn btn-action-delete btn-sm"
                [disabled]="deletingCategoryId === category.id"
                (click)="onDeleteCategory(category.id)"
                title="Delete category">
                @if (deletingCategoryId === category.id) {
                  <app-loading-spinner [inline]="true"></app-loading-spinner>
                } @else {
                  <i class="bi bi-trash3"></i>
                }
              </button>
            </li>
          }
        </ul>
      }
    </div>
  </div>
</ng-template>

<ng-template #manageFormulationTypesModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <div class="modal-header py-3">
      <h5 class="modal-title">Manage Formulation Types</h5>
      <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
    </div>

    <div class="modal-body p-4">
      <!-- Add new formulation type -->
      @if (!isAddingNewFormulationType) {
        <button type="button" class="btn btn-primary mb-3" (click)="onToggleAddFormulationType()">
          <i class="bi bi-plus-circle"></i>
          Add New Formulation Type
        </button>
      } @else {
        <div class="d-flex gap-2 mb-3">
          <input
            type="text"
            class="form-control form-control-sm"
            placeholder="Formulation name"
            [(ngModel)]="newFormulationLabel" />
          <button
            type="button"
            class="btn btn-primary btn-sm text-nowrap"
            [disabled]="!newFormulationLabel.trim() || isCreatingFormulationType"
            (click)="onCreateFormulationType(false)">
            @if (isCreatingFormulationType) {
              <app-loading-spinner [inline]="true" [textClass]="'text-white'"></app-loading-spinner>
            } @else {
              Create
            }
          </button>
          <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onToggleAddFormulationType()">
            Cancel
          </button>
        </div>
        @if (formulationCreateError) {
          <div class="text-danger small mb-3">{{ formulationCreateError }}</div>
        }
      }

      <!-- Formulation type list -->
      @if (formulationTypes.length === 0) {
        <p class="text-muted small text-center py-3 mb-0">No formulation types yet.</p>
      } @else {
        <ul class="list-group list-group-flush">
          @for (type of formulationTypes; track type.id) {
            <li class="list-group-item d-flex justify-content-between align-items-center px-0">
              <span>{{ type.label }}</span>
            </li>
          }
        </ul>
      }
    </div>
  </div>
</ng-template>

<ng-template #productModal let-modal>
  <div class="modal-content border-0 shadow-lg">
    <form [formGroup]="productForm" (ngSubmit)="onSubmitProduct()">
      <div class="modal-header py-3">
        <h5 class="modal-title">
          {{ isEditMode ? 'Update Product Details' : 'Register New Product' }}
        </h5>
        <button type="button" class="btn-close" (click)="modal.dismiss()"></button>
      </div>

      <div class="modal-body p-4">
        <div class="row g-4">
          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">
              Product Name
              <span class="text-danger">*</span>
            </label>
            <input type="text" class="form-control" formControlName="name" placeholder="e.g. Urea Fertilizer" />
            @if (isInvalid('name')) {
              <div class="text-danger small mt-1">Product name is required.</div>
            }
          </div>

          <div class="col-md-6">
            <label class="form-label small fw-bold text-muted">
              Category
              <span class="text-danger">*</span>
            </label>
            <ng-select
              placeholder="Select Category"
              [items]="productCategories"
              formControlName="product_category"
              bindValue="id"
              bindLabel="name"
              [clearable]="false"
              [searchable]="false" />
            @if (isInvalid('product_category')) {
              <div class="text-danger small mt-1">Category is required.</div>
            }

            @if (!isAddingNewCategory) {
              <button
                type="button"
                class="btn btn-link btn-sm p-0 mt-1 text-decoration-none"
                (click)="onToggleAddCategory()">
                <i class="bi bi-plus-circle me-1"></i>
                Add new category
              </button>
            } @else {
              <div class="d-flex gap-2 mt-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Category name"
                  [(ngModel)]="newCategoryName"
                  [ngModelOptions]="{ standalone: true }" />
                <button
                  type="button"
                  class="btn btn-primary btn-sm text-nowrap"
                  [disabled]="!newCategoryName.trim() || isCreatingCategory"
                  (click)="onCreateCategory()">
                  @if (isCreatingCategory) {
                    <app-loading-spinner [inline]="true" [textClass]="'text-white'"></app-loading-spinner>
                  } @else {
                    Create
                  }
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onToggleAddCategory()">
                  Cancel
                </button>
              </div>
            }
          </div>

          <div class="col-12">
            <label class="form-label small fw-bold text-muted">Composition / Contents</label>
            <textarea
              class="form-control"
              formControlName="contents"
              rows="2"
              placeholder="Describe chemical composition..."></textarea>
          </div>

          <div class="col-md-4">
            <label class="form-label small fw-bold text-muted">HSN Code</label>
            <input type="text" class="form-control" formControlName="hsn_code" placeholder="8-digit HSN" />
          </div>

          <div class="col-md-4">
            <label class="form-label small fw-bold text-muted">
              Formulation
              <span class="text-danger">*</span>
            </label>
            <ng-select
              placeholder="Select Formulation"
              [items]="formulationTypes"
              formControlName="formulation_type"
              bindValue="id"
              bindLabel="label"
              [clearable]="false"
              [searchable]="true" />
            @if (isInvalid('formulation_type')) {
              <div class="text-danger small mt-1">Formulation is required.</div>
            } @else {
              @if (getServerError('formulation_type'); as formulationError) {
                <div class="text-danger small mt-1">{{ formulationError }}</div>
              }
            }

            @if (!isAddingNewFormulationType) {
              <button
                type="button"
                class="btn btn-link btn-sm p-0 mt-1 text-decoration-none"
                (click)="onToggleAddFormulationType()">
                <i class="bi bi-plus-circle me-1"></i>
                Add new formulation type
              </button>
            } @else {
              <div class="d-flex gap-2 mt-2">
                <input
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Formulation name"
                  [(ngModel)]="newFormulationLabel"
                  [ngModelOptions]="{ standalone: true }" />
                <button
                  type="button"
                  class="btn btn-primary btn-sm text-nowrap"
                  [disabled]="!newFormulationLabel.trim() || isCreatingFormulationType"
                  (click)="onCreateFormulationType(true)">
                  @if (isCreatingFormulationType) {
                    <app-loading-spinner [inline]="true" [textClass]="'text-white'"></app-loading-spinner>
                  } @else {
                    Create
                  }
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm" (click)="onToggleAddFormulationType()">
                  Cancel
                </button>
              </div>
              @if (formulationCreateError) {
                <div class="text-danger small mt-1">{{ formulationCreateError }}</div>
              }
            }
          </div>

          <div class="col-md-4">
            <label class="form-label small fw-bold text-muted">Company</label>
            <ng-select
              placeholder="Assign Company"
              [items]="companies"
              formControlName="company"
              bindValue="id"
              bindLabel="name"
              [clearable]="true"
              [searchable]="false" />
            @if (isInvalid('company')) {
              <div class="text-danger small mt-1">Company is required.</div>
            }
          </div>

          <div class="col-12">
            <label class="form-label small fw-bold text-muted">Product Image</label>
            <div class="image-upload-zone border-dashed p-4 text-center bg-white">
              @if (!imagePreview) {
                <div (click)="fileInput.click()" class="cursor-pointer">
                  <i class="bi bi-cloud-arrow-up fs-1 text-primary mb-2 d-block"></i>
                  <p class="mb-0 small text-muted">Click to upload product photo</p>
                  <span class="text-xs text-muted">(Supports JPG, PNG up to 2MB)</span>
                </div>
              } @else {
                <div class="position-relative d-inline-block">
                  <img
                    [src]="imagePreview"
                    alt="Preview"
                    class="shadow-sm"
                    style="max-height: 150px; width: 100%; object-fit: contain" />
                  <button
                    type="button"
                    class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle shadow"
                    (click)="onRemoveImage()">
                    <i class="bi bi-trash3 small"></i>
                  </button>
                </div>
              }
              <input type="file" #fileInput hidden (change)="onImageSelected($event)" accept="image/*" />
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="modal.dismiss()">
          <i class="bi bi-x-circle"></i>
          <span>Cancel</span>
        </button>
        <button type="submit" class="btn btn-primary" [disabled]="productForm.invalid || isProductInfoSaving">
          @if (isProductInfoSaving) {
            <app-loading-spinner [inline]="true" [textClass]="'text-white'" class="me-2"></app-loading-spinner>
            Saving...
          } @else {
            <i class="bi bi-box-arrow-down"></i>
            {{ isEditMode ? 'Update' : 'Register' }}
          }
        </button>
      </div>
    </form>
  </div>
</ng-template>
